import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMemo } from "react";
import { Commodity } from "@/pages/sy/models/Commodity";
import { chargeOperationStyles } from "@/pages/sy/styles/ChargeOperationStyles";
import { formatCraneMagnitude } from "../utils/formatCraneMagnitude";
import { LayerAlignmentRow } from "../utils/layerAlignment";

export const TableRecipeLayers = ({
  commodities,
  alignedRows,
  recipeTargetWeight,
  suggestedLayerWeights,
  getCommodityColor,
  getBgColorWithAlpha,
  getRecipeLayerPercentage,
}: {
  commodities: Commodity[];
  alignedRows: LayerAlignmentRow[];
  recipeTargetWeight: number;
  suggestedLayerWeights?: ReadonlyMap<number, number> | null;
  getCommodityColor: (commodityId: number, commodities: Commodity[]) => string;
  getBgColorWithAlpha: (color: string, alpha: number) => string;
  getRecipeLayerPercentage: (weight: number, targetWeight: number) => number;
}) => {
  const theme = useTheme();
  const styles = useMemo(() => chargeOperationStyles(theme), [theme]);

  return (
    <TableContainer sx={styles.tableContainer}>
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={styles.tableHeader}>
            <TableCell width="15%" align="center">
              #
            </TableCell>
            <TableCell width="50%">Commodity</TableCell>
            <TableCell width="15%" align="center">
              %
            </TableCell>
            <TableCell width="20%" align="center">
              Weight
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {alignedRows.map(({ chargeLayer, recipeLayer: layer }, index) => {
            if (!layer) {
              return (
                <TableRow
                  key={`recipe-layer-gap-${chargeLayer?.ChargeLayerId ?? index}`}
                  aria-hidden="true"
                  sx={{
                    ...(styles.tableRow as object),
                    cursor: "default",
                    backgroundColor: "white",
                    "&:hover": { backgroundColor: "white" },
                  }}
                >
                  <TableCell colSpan={4}>{"\u00A0"}</TableCell>
                </TableRow>
              );
            }

            const commodityColor = getCommodityColor(
              layer.CommodityId as number,
              commodities,
            );
            const bgColor = getBgColorWithAlpha(commodityColor, 0.1);
            const suggestedWeight =
              Number(layer.Weight ?? 0) > 0 && layer.RecipeLayerId != null
                ? suggestedLayerWeights?.get(layer.RecipeLayerId)
                : undefined;
            const percentage = getRecipeLayerPercentage(
              layer.Weight,
              recipeTargetWeight,
            );
            const showSuggestedWeight = suggestedWeight != null;

            return (
              <TableRow
                key={layer.RecipeLayerId}
                sx={{
                  ...(styles.tableRow as object),
                  backgroundColor: bgColor,
                  cursor: "default",
                  "&:hover": {
                    backgroundColor: bgColor,
                  },
                }}
              >
                <TableCell align="center">{layer.LayerNumber}</TableCell>
                <TableCell>
                  <Box sx={styles.commodityCell}>
                    <Box
                      sx={{
                        ...(styles.colorIndicator as object),
                        backgroundColor: commodityColor,
                      }}
                    />
                    {layer.CommodityName}
                  </Box>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    color: (theme) => theme.palette.primary.main,
                    fontWeight: "bold",
                  }}
                >
                  {percentage.toFixed(1)}%
                </TableCell>
                <TableCell
                  align="center"
                  sx={showSuggestedWeight ? { py: "0 !important" } : undefined}
                >
                  {showSuggestedWeight ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        lineHeight: 1.15,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          color: "text.secondary",
                          fontSize: "1rem",
                          textDecoration: "line-through",
                        }}
                      >
                        {formatCraneMagnitude(layer.Weight, "0")}
                      </Box>
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        {formatCraneMagnitude(suggestedWeight, "0")}
                      </Box>
                    </Box>
                  ) : layer.Weight != null ? (
                    formatCraneMagnitude(layer.Weight, "0")
                  ) : (
                    "0"
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
